import React, { useEffect, useState } from 'react';
import { getErrorMessage, formatCurrency } from '@/lib/utils';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import { useExtensions } from '../hooks/useExtensions';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { 
  ArrowLeft,
  Building2,
  Calendar,
  Package,
  Truck,
  CheckCircle,
  Clock,
  FileText,
  Send,
  XCircle,
  ChefHat,
  Printer,
  Copy,
  Plus,
  Minus,
  Trash2,
  Save,
  Pencil,
  ArrowRight,
  AlertCircle,
  RotateCcw,
  MapPin,
  Navigation,
  CheckSquare,
  PackageCheck,
  ExternalLink,
  Phone,
  User,
  Camera,
  Search,
  Loader2
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import DeliveryProofDialog from '../components/DeliveryProofDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Definizione workflow ordini
const ORDER_STATUSES = [
  { value: 'draft', label: 'Bozza', icon: FileText, color: 'bg-gray-500/10 text-gray-600 border-gray-500/20' },
  { value: 'submitted', label: 'Inviato', icon: Send, color: 'bg-amber-500/10 text-amber-600 border-amber-500/20' },
  { value: 'confirmed', label: 'Confermato', icon: CheckCircle, color: 'bg-blue-500/10 text-blue-600 border-blue-500/20' },
  { value: 'in_preparation', label: 'In Preparazione', icon: ChefHat, color: 'bg-purple-500/10 text-purple-600 border-purple-500/20' },
  { value: 'ready', label: 'Pronto', icon: PackageCheck, color: 'bg-cyan-500/10 text-cyan-600 border-cyan-500/20' },
  { value: 'shipped', label: 'Spedito', icon: Truck, color: 'bg-indigo-500/10 text-indigo-600 border-indigo-500/20' },
  { value: 'in_delivery', label: 'In Consegna', icon: Navigation, color: 'bg-orange-500/10 text-orange-600 border-orange-500/20' },
  { value: 'delivered', label: 'Consegnato', icon: CheckSquare, color: 'bg-teal-500/10 text-teal-600 border-teal-500/20' },
  { value: 'completed', label: 'Completato', icon: CheckCircle, color: 'bg-green-500/10 text-green-600 border-green-500/20' },
  { value: 'cancelled', label: 'Annullato', icon: XCircle, color: 'bg-red-500/10 text-red-600 border-red-500/20' }
];

// Workflow ideale (stato corrente -> stato successivo consigliato)
const IDEAL_WORKFLOW = {
  'draft': 'submitted',
  'submitted': 'confirmed',
  'confirmed': 'in_preparation',
  'in_preparation': 'ready',
  'ready': 'shipped',
  'shipped': 'in_delivery',
  'in_delivery': 'delivered',
  'delivered': 'completed'
};

const OrderDetailPage = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const { t } = useI18n();
  const { isFeatureActive } = useExtensions();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editedLines, setEditedLines] = useState([]);
  const [editedNotes, setEditedNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [selectedNewStatus, setSelectedNewStatus] = useState('');
  
  // Aggiunta prodotti alla bozza
  const [showAddProductDialog, setShowAddProductDialog] = useState(false);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [loadingProducts, setLoadingProducts] = useState(false);
  
  // Tracking
  const [tracking, setTracking] = useState(null);
  const [showTrackingDialog, setShowTrackingDialog] = useState(false);
  const [trackingForm, setTrackingForm] = useState({
    status: '',
    tracking_number: '',
    carrier: '',
    notes: ''
  });
  const [updatingShipment, setUpdatingShipment] = useState(false);
  
  // Conferma Consegna (Proof)
  const [showProofDialog, setShowProofDialog] = useState(false);
  
  // Dialog conferma eliminazione
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  
  // Ricalcolo prezzi
  const [recalculatingPrices, setRecalculatingPrices] = useState(false);

  // Helper
  const isBuyer = user?.role === 'buyer';
  const isSeller = user?.role === 'seller';
  const hasProofFeature = isFeatureActive('ext_logistics', 'proof');
  
  // Check se ordine ha prezzi a €0 che potrebbero essere ricalcolati
  const hasZeroPrices = order?.status === 'draft' && order?.lines?.some((line: any) => line.unit_price === 0);

  useEffect(() => {
    fetchOrder();
  }, [id]);

  useEffect(() => {
    if (order) {
      fetchTracking();
    }
  }, [order?.id]);

  const fetchOrder = async () => {
    try {
      const res = await axios.get(`${API}/orders/${id}`);
      setOrder(res.data);
    } catch (error) {
      console.error('Error fetching order:', error);
      toast.error('Ordine non trovato');
      navigate('/orders');
    } finally {
      setLoading(false);
    }
  };

  const fetchTracking = async () => {
    try {
      const res = await axios.get(`${API}/logistics/order/${id}/tracking`);
      setTracking(res.data);
    } catch (error) {
      console.error('Error fetching tracking:', error);
    }
  };

  const handleUpdateShipment = async () => {
    if (!trackingForm.status) {
      toast.error('Seleziona uno stato');
      return;
    }
    
    setUpdatingShipment(true);
    try {
      await axios.put(`${API}/logistics/order/${id}/shipment`, trackingForm);
      toast.success('Spedizione aggiornata');
      setShowTrackingDialog(false);
      fetchOrder();
      fetchTracking();
    } catch (error) {
      toast.error(getErrorMessage(error, "Errore nell'aggiornamento"));
    } finally {
      setUpdatingShipment(false);
    }
  };

  const openTrackingDialog = () => {
    setTrackingForm({
      status: IDEAL_WORKFLOW[order?.status] || 'shipped',
      tracking_number: order?.tracking_number || '',
      carrier: order?.carrier || '',
      notes: ''
    });
    setShowTrackingDialog(true);
  };

  const handleStatusChange = async (action) => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/orders/${id}/${action}`);
      toast.success(`Ordine ${action === 'submit' ? 'inviato' : action === 'confirm' ? 'confermato' : 'aggiornato'}`);
      fetchOrder();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Errore'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateStatus = async (newStatus) => {
    setActionLoading(true);
    try {
      await axios.put(`${API}/orders/${id}`, { status: newStatus });
      const statusLabel = ORDER_STATUSES.find(s => s.value === newStatus)?.label || newStatus;
      toast.success(`Stato aggiornato a "${statusLabel}"`);
      setShowStatusDialog(false);
      setSelectedNewStatus('');
      fetchOrder();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Errore'));
    } finally {
      setActionLoading(false);
    }
  };

  // Ottiene lo stato successivo ideale nel workflow
  const getNextIdealStatus = () => {
    if (!order) return null;
    return IDEAL_WORKFLOW[order.status] || null;
  };

  // Ottiene la configurazione di uno stato
  const getStatusConfig = (statusValue) => {
    return ORDER_STATUSES.find(s => s.value === statusValue) || ORDER_STATUSES[0];
  };

  // Verifica se il cambio di stato è "indietro" nel workflow
  const isBackwardChange = (newStatus) => {
    if (!order) return false;
    const currentIndex = ORDER_STATUSES.findIndex(s => s.value === order.status);
    const newIndex = ORDER_STATUSES.findIndex(s => s.value === newStatus);
    return newIndex < currentIndex;
  };

  // Verifica se il cambio è un "salto" (non sequenziale)
  const isSkipChange = (newStatus) => {
    if (!order) return false;
    const nextIdeal = IDEAL_WORKFLOW[order.status];
    return newStatus !== nextIdeal && newStatus !== 'cancelled';
  };

  const handleDuplicate = async () => {
    setActionLoading(true);
    try {
      const res = await axios.post(`${API}/orders/${id}/duplicate`);
      toast.success('Ordine duplicato come bozza');
      navigate(`/orders/${res.data.id}`);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Errore nella duplicazione'));
    } finally {
      setActionLoading(false);
    }
  };

  const startEditMode = () => {
    setEditedLines([...order.lines]);
    setEditedNotes(order.notes || '');
    setEditMode(true);
  };

  const cancelEdit = () => {
    setEditMode(false);
    setEditedLines([]);
    setEditedNotes('');
  };

  const handleDeleteOrder = async () => {
    setActionLoading(true);
    try {
      await axios.delete(`${API}/orders/${id}`);
      toast.success('Bozza eliminata');
      // Dispatch event per aggiornare il badge nel layout
      window.dispatchEvent(new CustomEvent('order-updated'));
      navigate('/orders');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Errore nell\'eliminazione'));
    } finally {
      setActionLoading(false);
      setShowDeleteDialog(false);
    }
  };

  const updateLineQuantity = (index, delta) => {
    const newLines = [...editedLines];
    newLines[index].quantity = Math.max(1, newLines[index].quantity + delta);
    setEditedLines(newLines);
  };

  const removeLine = (index) => {
    if (editedLines.length <= 1) {
      toast.error('Deve esserci almeno un articolo');
      return;
    }
    setEditedLines(editedLines.filter((_, i) => i !== index));
  };

  // Cerca prodotti disponibili per l'aggiunta
  const fetchAvailableProducts = async (query = '') => {
    if (!order) return;
    setLoadingProducts(true);
    try {
      // Prima ottieni la company_id del seller
      const relRes = await axios.get(`${API}/relationships`);
      const relationships = relRes.data.relationships || relRes.data || [];
      const rel = relationships.find((r: any) => r.seller_id === order.seller_id);
      
      if (rel?.seller_company_id) {
        // Usa l'endpoint del catalogo pubblico
        const res = await axios.get(`${API}/products/catalog/${rel.seller_company_id}`, {
          params: { search: query, limit: 50 }
        });
        setAvailableProducts(res.data.products || res.data || []);
      } else {
        // Fallback: cerca prodotti tramite pricelist
        try {
          const pricelistRes = await axios.get(`${API}/pricelists/buyer/available`, {
            params: { seller_id: order.seller_id }
          });
          const products = pricelistRes.data.products || pricelistRes.data.items || [];
          setAvailableProducts(products);
        } catch {
          setAvailableProducts([]);
        }
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      setAvailableProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  };

  // Apri dialog per aggiungere prodotto
  const openAddProductDialog = () => {
    setShowAddProductDialog(true);
    setProductSearchQuery('');
    fetchAvailableProducts();
  };

  // Aggiungi prodotto all'ordine
  const addProductToOrder = (product) => {
    // Controlla se il prodotto è già nell'ordine
    const existingIndex = editedLines.findIndex(
      line => line.product_id === product.id || line.product_id === product.product_id
    );
    
    if (existingIndex >= 0) {
      // Incrementa la quantità
      const newLines = [...editedLines];
      newLines[existingIndex].quantity += 1;
      setEditedLines(newLines);
      toast.success(`Quantità aumentata per "${product.name || product.product_name}"`);
    } else {
      // Aggiungi nuovo prodotto
      const newLine = {
        product_id: product.id || product.product_id,
        product_name: product.name || product.product_name,
        quantity: 1,
        unit_price: product.price || product.unit_price || 0,
        unit_of_measure: product.unit || product.unit_of_measure || 'pz'
      };
      setEditedLines([...editedLines, newLine]);
      toast.success(`"${newLine.product_name}" aggiunto all'ordine`);
    }
    setShowAddProductDialog(false);
  };

  // Filtra prodotti in base alla ricerca
  const filteredProducts = availableProducts.filter(p => {
    const name = (p.name || p.product_name || '').toLowerCase();
    const query = productSearchQuery.toLowerCase();
    return name.includes(query);
  });
  
  // Ricalcola prezzi dall'attuale listino
  const recalculatePrices = async () => {
    setRecalculatingPrices(true);
    try {
      const res = await axios.post(`${API}/orders/${id}/recalculate-prices`);
      toast.success(`Prezzi aggiornati! ${res.data.prices_updated} prodotti ricalcolati. Nuovo totale: ${formatCurrency(res.data.new_total)}`);
      fetchOrder(); // Ricarica l'ordine con i nuovi prezzi
    } catch (error) {
      toast.error(getErrorMessage(error, 'Errore nel ricalcolo prezzi'));
    } finally {
      setRecalculatingPrices(false);
    }
  };

  const saveChanges = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/orders/${id}`, {
        lines: editedLines,
        notes: editedNotes
      });
      toast.success('Ordine aggiornato');
      setEditMode(false);
      fetchOrder();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Errore nel salvataggio'));
    } finally {
      setSaving(false);
    }
  };

  const calculateEditedTotal = () => {
    return editedLines.reduce((sum, line) => sum + (line.quantity * line.unit_price), 0);
  };

  const getEventIcon = (type) => {
    const icons = {
      created: FileText,
      created_from_ai: FileText,
      submitted: Send,
      confirmed: CheckCircle,
      updated: Clock,
      in_preparation: ChefHat,
      ready: Package,
      shipped: Truck,
      completed: CheckCircle,
      cancelled: XCircle
    };
    return icons[type] || Clock;
  };

  const getEventLabel = (type) => {
    const labels = {
      created: 'Ordine creato',
      created_from_ai: 'Ordine creato da AI',
      submitted: 'Ordine inviato',
      confirmed: 'Ordine confermato',
      updated: 'Ordine modificato',
      in_preparation: 'In preparazione',
      ready: 'Pronto per ritiro/consegna',
      shipped: 'Ordine spedito',
      completed: 'Ordine completato',
      cancelled: 'Ordine annullato'
    };
    return labels[type] || type;
  };

  // Genera il dettaglio dell'evento per la timeline
  const getEventDetail = (event) => {
    // Cambio di stato
    if (event.from_status && event.to_status) {
      const fromLabel = ORDER_STATUSES.find(s => s.value === event.from_status)?.label || event.from_status;
      const toLabel = ORDER_STATUSES.find(s => s.value === event.to_status)?.label || event.to_status;
      return `${fromLabel} → ${toLabel}`;
    }
    
    // Modifica generica con lista campi
    if (event.type === 'updated' && event.changes && event.changes.length > 0) {
      const changeLabels = {
        'lines': 'articoli',
        'notes': 'note',
        'delivery_date': 'data consegna',
        'status': 'stato',
        'total': 'totale'
      };
      const changedFields = event.changes
        .filter(c => c !== 'updated_at')
        .map(c => changeLabels[c] || c);
      if (changedFields.length > 0) {
        return `Modificato: ${changedFields.join(', ')}`;
      }
    }
    
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!order) return null;

  const statusConfig = getStatusConfig(order.status);
  const StatusIcon = statusConfig.icon;

  return (
    <div className="space-y-6" data-testid="order-detail-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/orders')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-heading font-bold flex items-center gap-3">
              Ordine #{order.id.slice(0, 8)}
              <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium border ${statusConfig.color}`}>
                <StatusIcon className="h-4 w-4" />
                {statusConfig.label}
              </span>
            </h1>
            <p className="text-muted-foreground">
              Creato il {new Date(order.created_at).toLocaleDateString('it-IT', { 
                day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' 
              })}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => window.print()}>
            <Printer className="mr-2 h-4 w-4" />
            Stampa
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={async () => {
              try {
                toast.info('Generazione PDF in corso...');
                const response = await axios.get(`${API}/orders/${id}/pdf`, {
                  responseType: 'blob',
                  headers: {
                    'Accept': 'application/pdf'
                  }
                });
                
                // Verifica che la risposta sia un PDF
                if (response.data.size === 0) {
                  toast.error('PDF vuoto ricevuto');
                  return;
                }
                
                const blob = new Blob([response.data], { type: 'application/pdf' });
                const url = window.URL.createObjectURL(blob);
                
                // Metodo alternativo più compatibile
                const link = document.createElement('a');
                link.style.display = 'none';
                link.href = url;
                link.setAttribute('download', `ordine_${id.slice(0, 8).toUpperCase()}.pdf`);
                document.body.appendChild(link);
                link.click();
                
                // Cleanup dopo un breve delay
                setTimeout(() => {
                  document.body.removeChild(link);
                  window.URL.revokeObjectURL(url);
                }, 100);
                
                toast.success('PDF scaricato!');
              } catch (error) {
                console.error('Errore download PDF:', error);
                toast.error('Errore nel download del PDF');
              }
            }}
            data-testid="download-pdf-btn"
          >
            <FileText className="mr-2 h-4 w-4" />
            PDF Ordine
          </Button>
          {isBuyer && (
            <Button variant="outline" size="sm" onClick={handleDuplicate} disabled={actionLoading} data-testid="reorder-btn">
              <Copy className="mr-2 h-4 w-4" />
              Riordina
            </Button>
          )}
          {/* Action buttons based on status and role */}
          {isBuyer && order.status === 'draft' && !editMode && (
            <>
              <Button variant="outline" size="sm" onClick={startEditMode} data-testid="edit-order-btn">
                <Pencil className="mr-2 h-4 w-4" />
                Modifica
              </Button>
              <Button 
                variant="destructive" 
                size="sm" 
                onClick={() => setShowDeleteDialog(true)}
                disabled={actionLoading}
                data-testid="delete-order-btn"
                title="Elimina bozza"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
              <Button onClick={() => handleStatusChange('submit')} disabled={actionLoading} data-testid="submit-order-btn">
                <Send className="mr-2 h-4 w-4" />
                Invia Ordine
              </Button>
            </>
          )}
          {isBuyer && order.status === 'draft' && editMode && (
            <>
              <Button variant="outline" size="sm" onClick={cancelEdit}>
                Annulla
              </Button>
              <Button onClick={saveChanges} disabled={saving} data-testid="save-changes-btn">
                <Save className="mr-2 h-4 w-4" />
                {saving ? 'Salvataggio...' : 'Salva Modifiche'}
              </Button>
            </>
          )}
          
          {/* Pulsante azione rapida per stato successivo ideale (solo seller) */}
          {isSeller && order.status === 'submitted' && (
            <Button onClick={() => handleStatusChange('confirm')} disabled={actionLoading} data-testid="confirm-order-btn">
              <CheckCircle className="mr-2 h-4 w-4" />
              Conferma
            </Button>
          )}
          {isSeller && getNextIdealStatus() && order.status !== 'submitted' && order.status !== 'completed' && order.status !== 'cancelled' && (
            <Button 
              onClick={() => handleUpdateStatus(getNextIdealStatus())} 
              disabled={actionLoading}
              data-testid="next-status-btn"
            >
              {(() => {
                const nextStatus = getStatusConfig(getNextIdealStatus());
                const NextIcon = nextStatus.icon;
                return (
                  <>
                    <NextIcon className="mr-2 h-4 w-4" />
                    {nextStatus.label}
                  </>
                );
              })()}
            </Button>
          )}
          
          {/* Pulsante per cambio stato manuale (solo seller, non per draft) */}
          {isSeller && order.status !== 'draft' && (
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setShowStatusDialog(true)}
              data-testid="change-status-btn"
            >
              <RotateCcw className="mr-2 h-4 w-4" />
              Cambia Stato
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Order Lines */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading flex items-center gap-2">
                  <Package className="h-5 w-5" />
                  Articoli ({editMode ? editedLines.length : order.lines?.length || 0})
                </CardTitle>
                {editMode && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={openAddProductDialog}
                    data-testid="add-product-btn"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Aggiungi Prodotto
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {/* Banner per ricalcolo prezzi se ci sono prodotti a €0 */}
              {hasZeroPrices && isBuyer && !editMode && (
                <div className="mx-4 mt-4 p-3 rounded-lg bg-amber-50 border border-amber-200 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 text-amber-700">
                    <AlertCircle className="h-4 w-4 flex-shrink-0" />
                    <span className="text-sm">Alcuni prodotti hanno prezzo €0,00. Puoi ricalcolare i prezzi dal listino attuale.</span>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-shrink-0 border-amber-300 text-amber-700 hover:bg-amber-100"
                    onClick={recalculatePrices}
                    disabled={recalculatingPrices}
                    data-testid="recalculate-prices-btn"
                  >
                    {recalculatingPrices ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-1" />
                    ) : (
                      <RotateCcw className="h-4 w-4 mr-1" />
                    )}
                    Ricalcola Prezzi
                  </Button>
                </div>
              )}
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="uppercase text-xs">Prodotto</TableHead>
                    <TableHead className="uppercase text-xs text-center">Qtà</TableHead>
                    <TableHead className="uppercase text-xs text-right">Prezzo Unit.</TableHead>
                    <TableHead className="uppercase text-xs text-right">Totale</TableHead>
                    {editMode && <TableHead className="w-12"></TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(editMode ? editedLines : order.lines)?.map((line, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{line.product_name}</p>
                          <p className="text-xs text-muted-foreground">{line.unit_of_measure}</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        {editMode ? (
                          <div className="flex items-center justify-center gap-2">
                            <Button 
                              variant="outline" 
                              size="icon" 
                              className="h-7 w-7"
                              onClick={() => updateLineQuantity(index, -1)}
                            >
                              <Minus className="h-3 w-3" />
                            </Button>
                            <span className="w-10 text-center font-mono">{line.quantity}</span>
                            <Button 
                              variant="outline" 
                              size="icon" 
                              className="h-7 w-7"
                              onClick={() => updateLineQuantity(index, 1)}
                            >
                              <Plus className="h-3 w-3" />
                            </Button>
                          </div>
                        ) : (
                          <span className="font-mono">{line.quantity}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(line.unit_price)}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(line.quantity * line.unit_price)}
                      </TableCell>
                      {editMode && (
                        <TableCell>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            className="text-destructive hover:text-destructive h-7 w-7"
                            onClick={() => removeLine(index)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="p-4 border-t bg-muted/30">
                <div className="flex justify-between items-center">
                  <span className="font-heading font-semibold">Totale Ordine</span>
                  <span className="text-2xl font-bold text-primary">
                    {formatCurrency(editMode ? calculateEditedTotal() : order.total)}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Notes */}
          {(order.notes || editMode) && (
            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-base">Note</CardTitle>
              </CardHeader>
              <CardContent>
                {editMode ? (
                  <Input
                    value={editedNotes}
                    onChange={(e) => setEditedNotes(e.target.value)}
                    placeholder="Aggiungi note..."
                    data-testid="edit-notes-input"
                  />
                ) : (
                  <p className="text-muted-foreground whitespace-pre-wrap">{order.notes}</p>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Order Info */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-base">Dettagli</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Building2 className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">
                    {isBuyer ? 'Fornitore' : 'Cliente'}
                  </p>
                  <p className="font-medium">{order.partner_company?.name || 'N/A'}</p>
                </div>
              </div>

              <Separator />

              {order.delivery_date && (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                    <Calendar className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Data Consegna</p>
                    <p className="font-medium">
                      {new Date(order.delivery_date).toLocaleDateString('it-IT')}
                    </p>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                  <Clock className="h-5 w-5 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Ultimo Aggiornamento</p>
                  <p className="font-medium">
                    {order.updated_at 
                      ? new Date(order.updated_at).toLocaleDateString('it-IT', {
                          day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
                        })
                      : new Date(order.created_at).toLocaleDateString('it-IT', {
                          day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
                        })
                    }
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tracking & Spedizione */}
          {tracking && (
            <Card className="border-primary/20">
              <CardHeader className="pb-3">
                <CardTitle className="font-heading text-base flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Truck className="h-5 w-5 text-primary" />
                    Tracking Spedizione
                  </span>
                  {isSeller && isFeatureActive('ext_logistics', 'tracking') && (
                    <Button size="sm" variant="outline" onClick={openTrackingDialog}>
                      Aggiorna Spedizione
                    </Button>
                  )}
                  {isSeller && hasProofFeature && (order?.status === 'in_delivery' || order?.status === 'delivered') && !order?.delivery_proof && (
                    <Button 
                      size="sm" 
                      className="bg-green-600 hover:bg-green-700"
                      onClick={() => setShowProofDialog(true)}
                      data-testid="open-proof-dialog-btn"
                    >
                      <CheckSquare className="h-4 w-4 mr-1" />
                      Conferma Consegna
                    </Button>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Info Corriere e Tracking */}
                {(order?.tracking_number || tracking?.carrier) && (
                  <div className="p-3 bg-muted/50 rounded-lg space-y-2">
                    {tracking?.carrier && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Corriere</span>
                        <span className="font-medium">{tracking.carrier.name}</span>
                      </div>
                    )}
                    {order?.tracking_number && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Tracking Number</span>
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-medium">{order.tracking_number}</span>
                          {tracking?.carrier?.tracking_link && (
                            <a 
                              href={tracking.carrier.tracking_link} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-primary hover:text-primary/80"
                            >
                              <ExternalLink className="h-4 w-4" />
                            </a>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Sito di Consegna */}
                {tracking?.delivery_site && (
                  <div className="p-3 bg-primary/5 border border-primary/20 rounded-lg">
                    <div className="flex items-start gap-3">
                      <MapPin className="h-5 w-5 text-primary mt-0.5" />
                      <div className="flex-1">
                        <div className="font-medium">{tracking.delivery_site.name}</div>
                        <div className="text-sm text-muted-foreground">
                          {tracking.delivery_site.address}, {tracking.delivery_site.postal_code} {tracking.delivery_site.city}
                        </div>
                        {tracking.delivery_site.contact_name && (
                          <div className="text-sm text-muted-foreground mt-1 flex items-center gap-3">
                            <span className="flex items-center gap-1">
                              <User className="h-3 w-3" />
                              {tracking.delivery_site.contact_name}
                            </span>
                            {tracking.delivery_site.contact_phone && (
                              <span className="flex items-center gap-1">
                                <Phone className="h-3 w-3" />
                                {tracking.delivery_site.contact_phone}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Timeline Tracking */}
                {tracking?.timeline?.length > 0 && (
                  <div className="space-y-3">
                    {tracking.timeline.slice().reverse().map((step, index) => {
                      const isCurrentStatus = step.status === order?.status;
                      const StepIcon = ORDER_STATUSES.find(s => s.value === step.status)?.icon || Package;
                      
                      return (
                        <div key={index} className={`flex gap-3 ${isCurrentStatus ? 'opacity-100' : 'opacity-60'}`}>
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            isCurrentStatus ? 'bg-primary text-primary-foreground' : 'bg-muted'
                          }`}>
                            <StepIcon className="h-4 w-4" />
                          </div>
                          <div className="flex-1">
                            <div className={`font-medium text-sm ${isCurrentStatus ? 'text-primary' : ''}`}>
                              {step.label_it}
                            </div>
                            {step.notes && (
                              <div className="text-xs text-muted-foreground">{step.notes}</div>
                            )}
                            <div className="text-xs text-muted-foreground">
                              {new Date(step.timestamp).toLocaleDateString('it-IT', {
                                day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
                              })}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Prova di Consegna */}
                {order?.delivery_proof && (
                  <div className="p-4 bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg">
                    <div className="flex items-center gap-2 text-green-700 dark:text-green-400 font-medium mb-2">
                      <CheckSquare className="h-4 w-4" />
                      Consegna Confermata
                    </div>
                    <div className="text-sm text-green-600 dark:text-green-500 space-y-1">
                      <div>
                        {new Date(order.delivery_proof.confirmed_at).toLocaleDateString('it-IT', {
                          day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
                        })}
                      </div>
                      {order.delivery_proof.signed_by && (
                        <div>Firmato da: {order.delivery_proof.signed_by}</div>
                      )}
                      {order.delivery_proof.notes && (
                        <div className="text-xs mt-1 text-muted-foreground">{order.delivery_proof.notes}</div>
                      )}
                      {order.delivery_proof.photo_url && (
                        <a 
                          href={order.delivery_proof.photo_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-2"
                        >
                          <Camera className="h-3 w-3" />
                          Visualizza foto
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Timeline */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-base">Timeline</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {order.events?.slice().reverse().map((event, index) => {
                  const EventIcon = getEventIcon(event.type);
                  const hasStatusChange = event.from_status && event.to_status;
                  const fromLabel = hasStatusChange ? ORDER_STATUSES.find(s => s.value === event.from_status)?.label : null;
                  const toLabel = hasStatusChange ? ORDER_STATUSES.find(s => s.value === event.to_status)?.label : null;
                  
                  return (
                    <div key={index} className="flex gap-3">
                      <div className="relative">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                          <EventIcon className="h-4 w-4 text-primary" />
                        </div>
                        {index < order.events.length - 1 && (
                          <div className="absolute top-8 left-1/2 -translate-x-1/2 w-0.5 h-6 bg-border" />
                        )}
                      </div>
                      <div className="flex-1 pb-4">
                        <p className="font-semibold text-sm">{getEventLabel(event.type)}</p>
                        {hasStatusChange && (
                          <p className="text-sm text-primary/80 font-medium">
                            {fromLabel} → {toLabel}
                          </p>
                        )}
                        {event.type === 'updated' && event.changes && (
                          <p className="text-sm text-muted-foreground">
                            Modificato: {event.changes
                              .filter(c => c !== 'updated_at')
                              .map(c => ({
                                'lines': 'articoli',
                                'notes': 'note',
                                'delivery_date': 'data consegna',
                                'status': 'stato',
                                'total': 'totale'
                              }[c] || c))
                              .join(', ')}
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(event.timestamp).toLocaleDateString('it-IT', {
                            day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Dialog Cambio Stato */}
      <Dialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <RotateCcw className="h-5 w-5" />
              Cambia Stato Ordine
            </DialogTitle>
            <DialogDescription>
              Seleziona il nuovo stato per l'ordine. Puoi procedere in avanti, tornare indietro o saltare stati.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {/* Stato attuale */}
            <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
              <span className="text-sm text-muted-foreground">Stato attuale:</span>
              {order && (() => {
                const current = getStatusConfig(order.status);
                const CurrentIcon = current.icon;
                return (
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm font-medium border ${current.color}`}>
                    <CurrentIcon className="h-3.5 w-3.5" />
                    {current.label}
                  </span>
                );
              })()}
            </div>

            {/* Stato successivo ideale */}
            {getNextIdealStatus() && (
              <div className="flex items-center gap-2 p-3 rounded-lg border border-green-500/30 bg-green-500/5">
                <ArrowRight className="h-4 w-4 text-green-600" />
                <span className="text-sm">
                  Prossimo stato ideale: <strong>{getStatusConfig(getNextIdealStatus()).label}</strong>
                </span>
              </div>
            )}

            {/* Selezione nuovo stato */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Nuovo stato:</label>
              <Select value={selectedNewStatus} onValueChange={setSelectedNewStatus}>
                <SelectTrigger data-testid="status-select">
                  <SelectValue placeholder="Seleziona stato..." />
                </SelectTrigger>
                <SelectContent>
                  {ORDER_STATUSES.filter(s => s.value !== 'draft' && s.value !== order?.status).map((status) => {
                    const StatusIcon = status.icon;
                    const isIdeal = status.value === getNextIdealStatus();
                    const isBack = isBackwardChange(status.value);
                    
                    return (
                      <SelectItem key={status.value} value={status.value}>
                        <div className="flex items-center gap-2">
                          <StatusIcon className="h-4 w-4" />
                          <span>{status.label}</span>
                          {isIdeal && (
                            <Badge variant="outline" className="ml-2 text-xs bg-green-500/10 text-green-600 border-green-500/20">
                              Consigliato
                            </Badge>
                          )}
                          {isBack && (
                            <Badge variant="outline" className="ml-2 text-xs bg-amber-500/10 text-amber-600 border-amber-500/20">
                              Indietro
                            </Badge>
                          )}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            {/* Avviso per cambio non standard */}
            {selectedNewStatus && (isBackwardChange(selectedNewStatus) || isSkipChange(selectedNewStatus)) && (
              <div className="flex items-start gap-2 p-3 rounded-lg border border-amber-500/30 bg-amber-500/5">
                <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5" />
                <div className="text-sm">
                  {isBackwardChange(selectedNewStatus) ? (
                    <span>Stai tornando a uno stato precedente. Questa azione verrà registrata nello storico.</span>
                  ) : (
                    <span>Stai saltando degli stati nel workflow. Assicurati che sia intenzionale.</span>
                  )}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowStatusDialog(false);
              setSelectedNewStatus('');
            }}>
              Annulla
            </Button>
            <Button 
              onClick={() => handleUpdateStatus(selectedNewStatus)}
              disabled={!selectedNewStatus || actionLoading}
              data-testid="confirm-status-change-btn"
            >
              {actionLoading ? 'Aggiornamento...' : 'Conferma Cambio'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Aggiorna Spedizione */}
      <Dialog open={showTrackingDialog} onOpenChange={setShowTrackingDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Truck className="h-5 w-5" />
              Aggiorna Spedizione
            </DialogTitle>
            <DialogDescription>
              Aggiorna lo stato di spedizione e i dati di tracking
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Stato */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Stato Spedizione</label>
              <Select 
                value={trackingForm.status} 
                onValueChange={(v) => setTrackingForm({...trackingForm, status: v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona stato..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="in_preparation">In Preparazione</SelectItem>
                  <SelectItem value="ready">Pronto per Ritiro</SelectItem>
                  <SelectItem value="shipped">Spedito</SelectItem>
                  <SelectItem value="in_delivery">In Consegna</SelectItem>
                  <SelectItem value="delivered">Consegnato</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Corriere */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Corriere</label>
              <Select 
                value={trackingForm.carrier} 
                onValueChange={(v) => setTrackingForm({...trackingForm, carrier: v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona corriere..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="internal">Consegna Diretta</SelectItem>
                  <SelectItem value="gls">GLS</SelectItem>
                  <SelectItem value="brt">BRT/Bartolini</SelectItem>
                  <SelectItem value="dhl">DHL</SelectItem>
                  <SelectItem value="sda">SDA/Poste</SelectItem>
                  <SelectItem value="ups">UPS</SelectItem>
                  <SelectItem value="fedex">FedEx</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Tracking Number */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Numero Tracking</label>
              <Input
                placeholder="Es: 1234567890"
                value={trackingForm.tracking_number}
                onChange={(e) => setTrackingForm({...trackingForm, tracking_number: e.target.value})}
              />
            </div>

            {/* Note */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Note</label>
              <Input
                placeholder="Note aggiuntive..."
                value={trackingForm.notes}
                onChange={(e) => setTrackingForm({...trackingForm, notes: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTrackingDialog(false)}>
              Annulla
            </Button>
            <Button 
              onClick={handleUpdateShipment}
              disabled={!trackingForm.status || updatingShipment}
            >
              {updatingShipment ? 'Aggiornamento...' : 'Aggiorna Spedizione'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Conferma Consegna (Proof) */}
      <DeliveryProofDialog
        open={showProofDialog}
        onOpenChange={setShowProofDialog}
        orderId={order?.id}
        onSuccess={() => {
          fetchOrder();
          fetchTracking();
        }}
      />
      
      {/* Dialog conferma eliminazione bozza */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare questa bozza?</AlertDialogTitle>
            <AlertDialogDescription>
              Questa azione non può essere annullata. L'ordine #{order?.order_number} verrà eliminato definitivamente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionLoading}>Annulla</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteOrder}
              disabled={actionLoading}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {actionLoading ? 'Eliminazione...' : 'Elimina'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog Aggiungi Prodotto */}
      <Dialog open={showAddProductDialog} onOpenChange={setShowAddProductDialog}>
        <DialogContent className="max-w-lg max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Plus className="h-5 w-5" />
              Aggiungi Prodotto
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 flex-1 overflow-hidden flex flex-col">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Cerca prodotto..."
                value={productSearchQuery}
                onChange={(e) => setProductSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="product-search-input"
              />
            </div>

            {/* Products List */}
            <div className="flex-1 overflow-y-auto -mx-6 px-6 max-h-[50vh]">
              {loadingProducts ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : filteredProducts.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Package className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>Nessun prodotto trovato</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredProducts.map((product) => {
                    const isInOrder = editedLines.some(
                      line => line.product_id === product.id || line.product_id === product.product_id
                    );
                    return (
                      <div
                        key={product.id || product.product_id}
                        className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                          isInOrder 
                            ? 'bg-primary/5 border-primary/20' 
                            : 'hover:bg-muted/50 border-transparent'
                        }`}
                      >
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{product.name || product.product_name}</p>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <span>{product.unit || product.unit_of_measure || 'pz'}</span>
                            <span>•</span>
                            <span className="font-medium text-primary">
                              {formatCurrency(product.price || product.unit_price || 0)}
                            </span>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant={isInOrder ? "secondary" : "default"}
                          onClick={() => addProductToOrder(product)}
                          data-testid={`add-product-${product.id || product.product_id}`}
                        >
                          {isInOrder ? (
                            <>
                              <Plus className="h-4 w-4 mr-1" />
                              +1
                            </>
                          ) : (
                            <>
                              <Plus className="h-4 w-4 mr-1" />
                              Aggiungi
                            </>
                          )}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddProductDialog(false)}>
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrderDetailPage;
